import React, { useState, useEffect, useCallback } from 'react';

interface AgentStatus {
  status: string;
  runningCount: number;
  desiredCount: number;
  lastEvent?: string;
  healthStatus?: string;
}

interface AgentsData {
  moltbot: AgentStatus;
  architect: AgentStatus;
  error: string | null;
}

interface SystemStatus {
  ecs: 'running' | 'degraded' | 'offline';
  notion: 'connected' | 'disconnected';
  discord: 'connected' | 'disconnected';
  tailscale: 'connected' | 'disconnected';
}

interface Agent {
  id: string;
  name: string;
  role: string;
  status: 'online' | 'offline' | 'busy' | 'starting';
  description: string;
  capabilities: string[];
  stats: {
    uptime: string;
    lastActive: string;
  };
}

function Agents() {
  const [agentsData, setAgentsData] = useState<AgentsData | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatus>({
    ecs: 'offline',
    notion: 'disconnected',
    discord: 'disconnected',
    tailscale: 'disconnected',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAgentsData = useCallback(async () => {
    try {
      const response = await fetch('/api/agents');
      if (!response.ok) {
        if (response.status === 401) {
          window.location.href = '/login';
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setAgentsData(data);

      // Update system status based on agent data
      setSystemStatus({
        ecs: data.moltbot?.status === 'running' ? 'running' :
             data.moltbot?.status === 'starting' ? 'degraded' : 'offline',
        notion: 'connected', // Will be verified by tasks API
        discord: 'disconnected', // Not yet implemented
        tailscale: data.moltbot?.status === 'running' ? 'connected' : 'disconnected',
      });

      setError(null);
    } catch (err) {
      console.error('Failed to fetch agents data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Also check Notion connectivity
  useEffect(() => {
    const checkNotion = async () => {
      try {
        const response = await fetch('/api/tasks');
        if (response.ok) {
          const data = await response.json();
          setSystemStatus(prev => ({
            ...prev,
            notion: data.error ? 'disconnected' : 'connected',
          }));
        }
      } catch {
        setSystemStatus(prev => ({ ...prev, notion: 'disconnected' }));
      }
    };
    checkNotion();
  }, []);

  useEffect(() => {
    fetchAgentsData();
    const interval = setInterval(fetchAgentsData, 15000); // Poll every 15s
    return () => clearInterval(interval);
  }, [fetchAgentsData]);

  const getAgentStatus = (status: string): 'online' | 'offline' | 'busy' | 'starting' => {
    if (status === 'running') return 'online';
    if (status === 'starting') return 'starting';
    if (status === 'degraded') return 'busy';
    return 'offline';
  };

  const getLastActive = (agent: AgentStatus | undefined): string => {
    if (!agent) return 'Unknown';
    if (agent.status === 'running') return 'Running now';
    if (agent.status === 'starting') return 'Starting up...';
    if (agent.status === 'not_deployed') return 'Not deployed';
    if (agent.lastEvent) return agent.lastEvent.substring(0, 40) + '...';
    return 'Offline';
  };

  const agents: Agent[] = [
    {
      id: 'moltbot',
      name: 'Moltbot',
      role: 'Gateway / Orchestrator',
      status: agentsData ? getAgentStatus(agentsData.moltbot?.status) : 'offline',
      description: 'Primary interface for user interactions. Routes tasks to appropriate agents and manages the overall system.',
      capabilities: ['Discord Integration', 'Task Routing', 'User Communication', 'Notion Integration'],
      stats: {
        uptime: agentsData?.moltbot?.healthStatus || '-',
        lastActive: agentsData ? getLastActive(agentsData.moltbot) : 'Loading...',
      },
    },
    {
      id: 'architect',
      name: 'Architect',
      role: '24/7 Coding Agent',
      status: agentsData ? getAgentStatus(agentsData.architect?.status) : 'offline',
      description: 'Autonomous coding agent that handles complex development tasks. Works continuously on assigned projects.',
      capabilities: ['Code Generation', 'Bug Fixing', 'Refactoring', 'Testing', 'Git Operations'],
      stats: {
        uptime: agentsData?.architect?.healthStatus || '-',
        lastActive: agentsData ? getLastActive(agentsData.architect) : 'Loading...',
      },
    },
  ];

  const statusConfig = {
    online: { color: 'bg-mc-success', label: 'Online', textColor: 'text-mc-success' },
    offline: { color: 'bg-mc-danger', label: 'Offline', textColor: 'text-mc-danger' },
    busy: { color: 'bg-mc-warning', label: 'Busy', textColor: 'text-mc-warning' },
    starting: { color: 'bg-mc-warning', label: 'Starting', textColor: 'text-mc-warning' },
  };

  return (
    <div>
      <header className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Agents</h1>
          <p className="text-mc-muted mt-2">Monitor and manage your MarketBrewer agents</p>
        </div>
        <button
          onClick={fetchAgentsData}
          className="text-mc-accent text-sm hover:underline"
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </header>

      {/* Agent Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {agents.map((agent) => (
          <div key={agent.id} className="card">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold">{agent.name}</h2>
                <p className="text-mc-muted">{agent.role}</p>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full bg-opacity-20 ${statusConfig[agent.status].color}`}>
                <div className={`status-dot ${statusConfig[agent.status].color}`}></div>
                <span className={`text-sm font-medium ${statusConfig[agent.status].textColor}`}>
                  {statusConfig[agent.status].label}
                </span>
              </div>
            </div>

            {/* Description */}
            <p className="text-mc-text mb-4">{agent.description}</p>

            {/* Capabilities */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-mc-muted mb-2">Capabilities</h4>
              <div className="flex flex-wrap gap-2">
                {agent.capabilities.map((cap) => (
                  <span
                    key={cap}
                    className="px-2 py-1 bg-mc-bg rounded text-xs text-mc-text"
                  >
                    {cap}
                  </span>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-mc-border">
              <div>
                <p className="text-2xl font-bold text-mc-success">{agent.stats.uptime}</p>
                <p className="text-xs text-mc-muted">Health</p>
              </div>
              <div>
                <p className="text-sm font-medium truncate">{agent.stats.lastActive}</p>
                <p className="text-xs text-mc-muted">Status</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* System Status */}
      <div className="card mt-6">
        <h3 className="text-lg font-semibold mb-4">System Status</h3>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse flex items-center gap-3 p-3 bg-mc-bg rounded-lg">
                <div className="w-3 h-3 bg-mc-border rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 w-20 bg-mc-border rounded mb-1"></div>
                  <div className="h-3 w-16 bg-mc-border rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3 p-3 bg-mc-bg rounded-lg">
              <div className={`status-dot ${systemStatus.ecs === 'running' ? 'status-online' : systemStatus.ecs === 'degraded' ? 'status-busy' : 'status-offline'}`}></div>
              <div>
                <p className="font-medium">AWS ECS</p>
                <p className="text-xs text-mc-muted">{systemStatus.ecs === 'running' ? 'Running' : systemStatus.ecs === 'degraded' ? 'Degraded' : 'Offline'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-mc-bg rounded-lg">
              <div className={`status-dot ${systemStatus.notion === 'connected' ? 'status-online' : 'status-offline'}`}></div>
              <div>
                <p className="font-medium">Notion API</p>
                <p className="text-xs text-mc-muted">{systemStatus.notion === 'connected' ? 'Connected' : 'Disconnected'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-mc-bg rounded-lg">
              <div className={`status-dot ${systemStatus.discord === 'connected' ? 'status-online' : 'status-offline'}`}></div>
              <div>
                <p className="font-medium">Discord</p>
                <p className="text-xs text-mc-muted">{systemStatus.discord === 'connected' ? 'Connected' : 'Not configured'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-mc-bg rounded-lg">
              <div className={`status-dot ${systemStatus.tailscale === 'connected' ? 'status-online' : 'status-offline'}`}></div>
              <div>
                <p className="font-medium">Tailscale</p>
                <p className="text-xs text-mc-muted">{systemStatus.tailscale === 'connected' ? 'Connected' : 'Disconnected'}</p>
              </div>
            </div>
          </div>
        )}
        {error && (
          <p className="mt-2 text-sm text-mc-danger">Error fetching status: {error}</p>
        )}
      </div>
    </div>
  );
}

export default Agents;
